import React, { useEffect, useState } from 'react';
import { Box, keyframes } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

// Firework explosion animation
const fireworkExplode = keyframes`
  0% {
    transform: translate(0, 0) scale(0);
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) scale(1);
    opacity: 0;
  }
`;

// Glitter twinkle and fall
const glitterFall = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) rotate(var(--r)) scale(0.3);
    opacity: 0;
  }
`;

// Shimmer effect
const shimmer = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

/**
 * CelebrationAnimation - Fireworks followed by glitter
 * @param {Object} location - { lat, lng } celebration location
 * @param {boolean} active - Whether animation is active
 * @param {Object} mapRef - Reference to map
 * @param {Function} onComplete - Callback when animation completes
 */
function CelebrationAnimation({ location, active, mapRef, onComplete }) {
  const [position, setPosition] = useState(null);
  const [fireworks, setFireworks] = useState([]);
  const [glitter, setGlitter] = useState([]);
  const [showFireworks, setShowFireworks] = useState(true);

  useEffect(() => {
    if (active && location && mapRef) {
      const map = mapRef.getMap();
      if (!map) return;

      // Use Google Maps overlay to get screen coordinates
      const overlay = new window.google.maps.OverlayView();
      overlay.draw = function() {};
      overlay.onAdd = function() {
        const projection = this.getProjection();
        if (projection) {
          const latLng = new window.google.maps.LatLng(location.lat, location.lng);
          const point = projection.fromLatLngToContainerPixel(latLng);

          if (point) {
            setPosition({ x: point.x, y: point.y });

            // Generate fireworks (3 bursts)
            const fireworksData = [];
            const colors = ['#FFD700', '#FF69B4', '#00CED1', '#FF6347', '#9370DB'];

            for (let burst = 0; burst < 3; burst++) {
              const particleCount = 12;
              for (let i = 0; i < particleCount; i++) {
                const angle = (Math.PI * 2 * i) / particleCount;
                const distance = 50 + Math.random() * 20;
                fireworksData.push({
                  id: `fw-${burst}-${i}`,
                  tx: Math.cos(angle) * distance,
                  ty: Math.sin(angle) * distance,
                  color: colors[Math.floor(Math.random() * colors.length)],
                  delay: burst * 0.3,
                });
              }
            }
            setFireworks(fireworksData);

            // Generate glitter after fireworks
            setTimeout(() => {
              setShowFireworks(false);
              const glitterData = [];
              for (let i = 0; i < 20; i++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * 40;
                glitterData.push({
                  id: `gl-${i}`,
                  tx: Math.cos(angle) * distance,
                  ty: Math.sin(angle) * distance + 60, // Fall downwards
                  rotation: Math.random() * 360,
                  delay: Math.random() * 0.5,
                  icon: Math.random() > 0.5 ? 'star' : 'sparkle',
                });
              }
              setGlitter(glitterData);
            }, 1200); // Show glitter after fireworks
          }
        }
      };
      overlay.onRemove = function() {};
      overlay.setMap(map);

      // Complete animation after total duration
      const timer = setTimeout(() => {
        try {
          overlay.setMap(null);
        } catch (e) {
          // Ignore cleanup errors
        }
        if (onComplete) {
          onComplete();
        }
      }, 3500); // 1.2s fireworks + 2.3s glitter

      return () => {
        clearTimeout(timer);
        try {
          overlay.setMap(null);
        } catch (e) {
          // Ignore cleanup errors
        }
      };
    }
  }, [active, location, mapRef, onComplete]);

  if (!active || !position) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1002,
      }}
    >
      {/* Fireworks particles */}
      {showFireworks && fireworks.map((fw) => (
        <Box
          key={fw.id}
          sx={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            width: '8px',
            height: '8px',
            marginLeft: '-4px',
            marginTop: '-4px',
            borderRadius: '50%',
            backgroundColor: fw.color,
            boxShadow: `0 0 10px ${fw.color}`,
            '--tx': `${fw.tx}px`,
            '--ty': `${fw.ty}px`,
            animation: `${fireworkExplode} 0.8s ease-out ${fw.delay}s`,
          }}
        />
      ))}

      {/* Glitter particles */}
      {!showFireworks && glitter.map((gl) => (
        <Box
          key={gl.id}
          sx={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            marginLeft: '-12px',
            marginTop: '-12px',
            '--tx': `${gl.tx}px`,
            '--ty': `${gl.ty}px`,
            '--r': `${gl.rotation}deg`,
            animation: `${glitterFall} 2s ease-out ${gl.delay}s, ${shimmer} 0.5s ease-in-out infinite ${gl.delay}s`,
          }}
        >
          {gl.icon === 'star' ? (
            <StarIcon
              sx={{
                fontSize: '20px',
                color: '#FFD700',
                filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 0.8))',
              }}
            />
          ) : (
            <AutoAwesomeIcon
              sx={{
                fontSize: '18px',
                color: '#FFF',
                filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.8))',
              }}
            />
          )}
        </Box>
      ))}

      {/* Center glow during fireworks */}
      {showFireworks && (
        <Box
          sx={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            width: '60px',
            height: '60px',
            marginLeft: '-30px',
            marginTop: '-30px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 215, 0, 0.3)',
            boxShadow: '0 0 40px rgba(255, 215, 0, 0.6)',
            animation: `${shimmer} 0.4s ease-in-out infinite`,
          }}
        />
      )}
    </Box>
  );
}

export default CelebrationAnimation;
